import svgQuote from '@img/icons/background/quote.svg?url';
import { css } from 'styled-components';
import { transition } from './animate';
import brand from './brand';
import { fluid } from './fluid';
import fonts from './fonts';
import scrollbars from './scrollbars';
export const displayStyles = css `
  ${fonts.berlin.bold};

  color: currentColor;
  margin: 0 0 ${fluid(20, 24)};
  letter-spacing: 0.02em;
`;
export const display1 = css `
  ${displayStyles};

  font-size: ${fluid(44, 64)};
  line-height: ${fluid(40, 58)};
`;
export const display2 = css `
  ${displayStyles};

  font-size: ${fluid(40, 56)};
  line-height: ${fluid(36, 52)};
`;
export const display3 = css `
  ${displayStyles};

  font-size: ${fluid(32, 48)};
  line-height: ${fluid(30, 44)};
`;
export const headingStyles = css `
  ${fonts.aptos.extraBold};

  color: currentColor;
  margin: 0 0 ${fluid(24, 32)};
  letter-spacing: 0.02em;
`;
export const h1 = css `
  ${headingStyles};

  font-size: ${fluid(28, 48)};
  line-height: ${fluid(28, 48)};
`;
export const h2 = css `
  ${headingStyles};

  font-size: ${fluid(24, 32)};
  line-height: ${fluid(24, 32)};
`;
export const h3 = css `
  ${headingStyles};

  font-size: ${fluid(20, 28)};
  line-height: ${fluid(20, 28)};
`;
export const h4 = css `
  ${headingStyles};

  font-size: ${fluid(18, 24)};
  line-height: ${fluid(18, 24)};
`;
export const h5 = css `
  ${headingStyles};

  font-size: ${fluid(18, 20)};
  line-height: ${fluid(18, 20)};
`;
const paragraphStyles = css `
  ${fonts.aptos.regular};

  color: currentColor;
  margin: 0 0 ${fluid(16, 24)};
`;
export const paragraphLarge = css `
  ${paragraphStyles};

  font-size: ${fluid(18, 20)};
  line-height: ${fluid(26, 28)};
`;
export const paragraphRegular = css `
  ${paragraphStyles};

  font-size: ${fluid(14, 18)};
  line-height: ${fluid(24, 26)};
`;
export const paragraphSmall = css `
  ${paragraphStyles};

  font-size: ${fluid(14, 16)};
  line-height: ${fluid(22, 24)};
`;
export const paragraphExtraSmall = css `
  ${paragraphStyles};

  font-size: ${fluid(12, 14)};
  line-height: ${fluid(18, 22)};
`;
export const paragraphTiny = css `
  ${paragraphStyles};

  font-size: ${fluid(10, 12)};
  line-height: ${fluid(16, 18)};
`;
export const link = css `
  ${transition('text-decoration-color')};
  --textLinkColour: ${brand.global.black};
  ${fonts.aptos.regular};

  display: inline-block;
  color: var(--textLinkColour);
  text-decoration: underline;
  text-decoration-color: currentColor;
  text-decoration-thickness: 1px;
  text-underline-offset: 1px;

  &:focus {
    outline: none;
  }

  &:focus-visible {
    outline: auto;
    outline-offset: 2px;
    outline-width: 1px;
    outline-style: solid;
  }

  &:focus,
  &:hover {
    text-decoration-color: transparent;
  }
`;
export const linkOpposite = css `
  ${link};

  text-decoration-color: transparent;

  &:focus,
  &:hover {
    text-decoration-color: currentColor;
  }
`;
export const blockquote = css `
  ${paragraphLarge};

  color: currentColor;
  text-align: left;
  position: relative;
  padding-left: ${fluid(58, 80)};
  background-size: ${fluid(42, 56)};
  background-repeat: no-repeat;
  background-position: top left;
  background-image: url(${svgQuote});
`;
export const table = css `
  ${paragraphRegular};

  background: ${brand.global.white};
  border-collapse: collapse;
  color: currentColor;
  margin-bottom: 1em;
  width: 100% !important;

  td,
  th {
    border-right: 2px solid ${brand.global.white};
    padding: 12px 16px;
    text-align: left;
  }

  thead {
    tr {
      background: ${brand.grey.grey13};

      td,
      th {
        ${fonts.aptos.bold};

        color: ${brand.global.white};
        text-transform: uppercase;
      }
    }
  }

  tbody {
    tr {
      &:nth-child(odd) {
        background-color: ${brand.grey.grey96};
      }

      &:nth-child(even) {
        background-color: ${brand.grey.grey90};
      }
    }
  }
`;
export const contentStyles = css `
  .display1-styles {
    ${display1};
  }

  .display2-styles {
    ${display2};
  }

  .display3-styles {
    ${display3};
  }

  h1,
  .h1-styles {
    ${h1};
  }

  h2,
  .h2-styles {
    ${h2};
  }

  h3,
  .h3-styles {
    ${h3};
  }

  h4,
  .h4-styles {
    ${h4}
  }

  h5,
  .h5-styles {
    ${h5}
  }

  h1,
  .h1-styles,
  h2,
  .h2-styles,
  h3,
  .h3-styles,
  h4,
  .h4-styles,
  h5,
  .h5-styles {
    margin: ${fluid(24, 32)} 0 ${fluid(16, 24)};
  }

  p {
    ${paragraphRegular}

    a {
      ${link};
    }
  }

  b,
  strong {
    ${fonts.aptos.bold};

    em {
      ${fonts.aptos.extraBoldItalic};
    }
  }

  em {
    ${fonts.aptos.regularItalic};

    b,
    strong {
      ${fonts.aptos.extraBoldItalic};
    }
  }

  .large {
    ${paragraphLarge}
  }

  .small {
    ${paragraphSmall}
  }

  .x-small {
    ${paragraphExtraSmall};
  }

  .tiny {
    ${paragraphTiny};
  }

  a {
    ${link}
  }

  ol,
  ul {
    ${paragraphRegular};

    margin: ${fluid(8, 12)} 0 ${fluid(24, 32)};

    li {
      color: currentColor;
      margin: 0 0 ${fluid(8, 12)};
    }
  }

  ol,
  ul {
    list-style: none;
    padding-left: 1.2em;

    li::before {
      color: ${brand.grey.grey20};
      display: inline-block;
      width: 1.2em;
      margin-left: -1.2em;
    }
  }

  ol {
    counter-reset: li;

    li {
      counter-increment: li;

      &::before {
        content: counter(li) '.';
        width: 2.5ch; // Allow space for double digit numbers
      }
    }
  }

  // Ensure that inline styling hasn't been applied
  ul:not([style*='list-style-type']) li::before {
    content: '\\2022';
    font-weight: bold;
  }

  blockquote {
    ${blockquote};
  }

  hr {
    display: block;
    background: ${brand.grey.grey40};
    border: 0;
    height: 2px;
    margin: ${fluid(24, 32)} 0;
    padding: 0;
    width: 100%;
  }

  table {
    ${table};
  }

  .responsive-table {
    ${scrollbars};

    position: relative;
    overflow-x: auto;
  }

  > *:first-child {
    margin-top: 0;
  }

  > *:last-child {
    margin-bottom: 0;
  }
`;
